import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ChevronLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <FileText className="h-6 w-6 text-emerald-500" />
            <h1 className="text-xl font-bold text-slate-900">Terms of Service</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
          <p className="text-sm text-slate-500 mb-6">Last updated: February 16, 2026</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              By accessing or using StelliSocial, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Description of Service</h2>
            <p className="text-slate-600 leading-relaxed">
              StelliSocial is a social media management platform that allows you to schedule, publish, and analyze content across multiple social media platforms including Facebook, Instagram, LinkedIn, Twitter/X, TikTok, YouTube, Pinterest, Snapchat, Threads, Bluesky, and Google Business Profile.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Account Registration</h2>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You must be at least 18 years old to use this service</li>
              <li>One person or entity may not maintain more than one account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Social Media Account Connections</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              When you connect your social media accounts to StelliSocial:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>You authorize us to access and post to your accounts on your behalf</li>
              <li>You remain responsible for all content posted through our service</li>
              <li>You must comply with each platform's terms of service</li>
              <li>You can disconnect accounts at any time through your settings</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Acceptable Use</h2>
            <p className="text-slate-600 leading-relaxed mb-4">You agree not to use StelliSocial to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Post illegal, harmful, or offensive content</li>
              <li>Violate any third party's intellectual property rights</li>
              <li>Spam or send unsolicited messages</li>
              <li>Impersonate others or misrepresent your affiliation</li>
              <li>Interfere with or disrupt the service</li>
              <li>Attempt to gain unauthorized access to other accounts</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Subscription and Billing</h2>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Paid subscriptions are billed in advance on a monthly or annual basis</li>
              <li>You can cancel your subscription at any time</li>
              <li>Refunds are provided in accordance with our refund policy</li>
              <li>We reserve the right to modify pricing with 30 days notice</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Intellectual Property</h2>
            <p className="text-slate-600 leading-relaxed">
              You retain ownership of content you create and post through StelliSocial. You grant us a limited license to use, store, and transmit your content solely for the purpose of providing our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed">
              StelliSocial is provided "as is" without warranties of any kind. We are not liable for any indirect, incidental, or consequential damages arising from your use of the service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Termination</h2>
            <p className="text-slate-600 leading-relaxed">
              We may terminate or suspend your account at any time for violations of these terms. Upon termination, your right to use the service ceases immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Changes to Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              We reserve the right to modify these terms at any time. We will notify you of significant changes via email or through the service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Contact</h2>
            <p className="text-slate-600 leading-relaxed">
              For questions about these Terms of Service, contact us at:
            </p>
            <p className="text-slate-600 mt-2">
              <strong>Email:</strong> legal@stellisocial.com
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default TermsOfServicePage;
